import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { billingService, type SubscriptionInfoResponse, type UsageResponse } from '@/services/billing'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgSlug: string
  /** Current gate state. When omitted the dialog fetches it. */
  usage?: UsageResponse | null
}

/**
 * Upgrade dialog for BOTH transitions:
 *
 *  - **Free → paid** (no Stripe subscription): the org must add a payment method,
 *    so the CTA opens Stripe Checkout.
 *  - **Paid tier → higher tier** (subscription exists): the card is already on
 *    file, so the CTA approves the upgrade in place — we set the subscription's
 *    quantity to the new band and Stripe invoices the difference immediately.
 *
 * The org is capped at the band it has committed to; volume beyond it is unbilled,
 * which is why access is blocked until the upgrade is approved.
 */
export function BillingUpgradeDialog({ open, onOpenChange, orgSlug, usage: usageProp }: Props) {
  const [info, setInfo] = useState<SubscriptionInfoResponse | null>(null)
  const [usage, setUsage] = useState<UsageResponse | null>(usageProp ?? null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    billingService.getSubscriptionInfo().then(setInfo).catch(() => {})
    if (usageProp) setUsage(usageProp)
    else billingService.getUsage().then(setUsage).catch(() => {})
  }, [open, usageProp])

  const hasSubscription = usage ? usage.is_free === false : false
  const committedTier = usage?.committed_tier ?? null
  const requiredTier = usage?.required_tier ?? null
  const requiredPrice = usage?.required_tier_price ?? null
  const totalRecords = (usage?.mtd_records ?? 0) + (usage?.mtd_backfill_records ?? 0)

  const startCheckout = async () => {
    const { url } = await billingService.createCheckoutSession({
      success_path: `/${orgSlug}/settings?upgraded=1`,
      cancel_path: `/${orgSlug}`,
    })
    if (!url) throw new Error('No checkout URL returned by the billing service')
    window.location.href = url
  }

  const approveUpgrade = async () => {
    const result = await billingService.approveUpgrade(requiredTier ?? undefined)
    toast.success(`Upgraded to ${result.tier} — $${result.monthly_price.toLocaleString()}/mo`)
    onOpenChange(false)
    // Reload so the navbar badge and the gate state refresh together.
    window.location.reload()
  }

  const onConfirm = async () => {
    if (loading) return
    setLoading(true)
    try {
      if (hasSubscription) await approveUpgrade()
      else await startCheckout()
    } catch (error: any) {
      const status = error?.response?.status
      const detail: string = error?.response?.data?.detail || error?.message || ''
      // Paid org whose card is missing/expired → open the portal to add one.
      if (status === 402 || detail.includes('no_payment_method')) {
        toast.message('Add a payment method to complete the upgrade.')
        try {
          const { url } = await billingService.createPortalSession(`/${orgSlug}/upgrade`)
          if (url) {
            window.location.href = url
            return
          }
        } catch {
          /* fall through */
        }
      }
      console.error('Upgrade failed:', error)
      toast.error(detail || 'Could not complete the upgrade. Please try again.')
      setLoading(false)
    }
  }

  // Paid bands only (hide the free $0 band).
  const tiers = (info?.tiers ?? []).filter((t) => t.price > 0).sort((a, b) => a.price - b.price)

  const title = hasSubscription ? 'Approve your plan upgrade' : 'Upgrade to a paid plan'
  const description = hasSubscription ? (
    <>
      Your volume has passed the <strong>{committedTier}</strong> band you're currently billed for.
      Approve the move to <strong>{requiredTier}</strong>
      {requiredPrice != null && (
        <>
          {' '}
          at <strong>${requiredPrice.toLocaleString()}/mo</strong>
        </>
      )}{' '}
      to keep processing. Your card on file is charged the difference immediately.
    </>
  ) : (
    <>
      You're on the free trial. Add a payment method to keep processing beyond the free limit. You'll
      start on the <strong>{requiredTier ?? '100K'}</strong> band; if your volume later passes it, we'll
      ask you to approve the next band before you're charged more.
    </>
  )

  const ctaLabel = loading
    ? hasSubscription
      ? 'Approving…'
      : 'Opening…'
    : hasSubscription
      ? 'Approve upgrade'
      : 'Add payment method'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {usage && (
          <div className="rounded-lg border border-muted p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Records this month</span>
              <span className="font-medium">{totalRecords.toLocaleString()}</span>
            </div>
            {committedTier && (
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Current band</span>
                <span className="font-medium">{committedTier}</span>
              </div>
            )}
            {requiredTier && requiredTier !== committedTier && (
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Required band</span>
                <span className="font-semibold">
                  {requiredTier}
                  {requiredPrice != null && ` — $${requiredPrice.toLocaleString()}/mo`}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="py-2">
          <p className="text-xs font-medium text-muted-foreground mb-2">Monthly volume pricing</p>
          <div className="grid grid-cols-2 gap-2 max-h-[38vh] overflow-auto" aria-hidden={loading}>
            {tiers.map((tier) => {
              const isCurrent = tier.tier_name === committedTier
              const isTarget = tier.tier_name === requiredTier
              return (
                <div
                  key={tier.tier_name}
                  className={`p-3 rounded-lg border text-left ${
                    isTarget
                      ? 'border-primary bg-primary/5'
                      : isCurrent
                        ? 'border-muted-foreground/40'
                        : 'border-muted'
                  }`}
                >
                  <p className="font-medium text-sm">
                    {tier.tier_name}
                    {isCurrent && <span className="ml-1 text-xs text-muted-foreground">(current)</span>}
                    {isTarget && !isCurrent && <span className="ml-1 text-xs text-primary">(required)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {tier.base_volume.toLocaleString()} - {tier.cap_volume.toLocaleString()} records
                  </p>
                  <p className="text-sm font-semibold mt-1">${tier.price.toLocaleString()}/mo</p>
                </div>
              )
            })}
            {tiers.length === 0 && (
              <p className="col-span-2 text-sm text-muted-foreground">Loading pricing…</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {hasSubscription
              ? 'Charged to your card on file. The difference is prorated and invoiced now.'
              : "You'll enter your card details securely on Stripe."}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={onConfirm} disabled={loading}>
              {ctaLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
